import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { UserMultiSelect } from '../PushNotifications/UserMultiSelect';
import { EmailHistory } from './EmailHistory';
import { InactiveUserPicker } from './InactiveUserPicker';
import {
  callSendEmailToUsers,
  type EmailCategory,
  type EmailTarget,
  type SendEmailResult,
} from '../../services/firebase';
import type { FirestoreUser } from '../../types';

const SUBJECT_MAX = 150;
const MAX_SELECTION = 100;

type TargetMode = 'all' | 'specific' | 'inactive';

/**
 * Admin email composer.
 *
 * Mirrors PushNotifications deliberately — same compose/confirm/test/history
 * shape — so the two broadcast surfaces do not need learning twice.
 *
 * The important difference is what "All users" means. For push it means every
 * user. Here it means every user who has CONSENTED to the chosen category, and
 * the server decides that, not this component. The result panel therefore
 * always reports `resolvedUserCount` alongside `queuedCount`: the gap between
 * them is the consent filter doing its job, and showing only the second number
 * would make a working system look broken.
 */
export function EmailSender() {
  const { user } = useAuth();

  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [htmlBody, setHtmlBody] = useState('');
  const [useHtml, setUseHtml] = useState(false);
  const [category, setCategory] = useState<EmailCategory>('marketing');
  const [targetMode, setTargetMode] = useState<TargetMode>('specific');
  const [selectedUsers, setSelectedUsers] = useState<FirestoreUser[]>([]);
  const [inactiveUids, setInactiveUids] = useState<string[]>([]);

  const [sending, setSending] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<SendEmailResult | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  const subjectOver = subject.length > SUBJECT_MAX;
  const subjectEmpty = subject.trim().length === 0;
  const bodyEmpty = body.trim().length === 0;
  const htmlEmpty = useHtml && htmlBody.trim().length === 0;

  const specificNoUsers = targetMode === 'specific' && selectedUsers.length === 0;
  const inactiveNoUsers = targetMode === 'inactive' && inactiveUids.length === 0;

  const sendDisabled =
    subjectEmpty ||
    bodyEmpty ||
    subjectOver ||
    htmlEmpty ||
    specificNoUsers ||
    inactiveNoUsers;

  const inputClass =
    'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

  const recipientSummary = useMemo(() => {
    if (targetMode === 'all') return 'All consenting users';
    const n = targetMode === 'specific' ? selectedUsers.length : inactiveUids.length;
    return `${n} user${n === 1 ? '' : 's'} (before consent filtering)`;
  }, [targetMode, selectedUsers.length, inactiveUids.length]);

  const resolveTarget = (): EmailTarget => {
    if (targetMode === 'all') return 'all';
    if (targetMode === 'inactive') return { userIds: inactiveUids };
    return { userIds: selectedUsers.map((u) => u.uid) };
  };

  const buildParams = (target: EmailTarget, isTest: boolean) => ({
    subject: subject.trim(),
    body: body.trim(),
    ...(useHtml && htmlBody.trim() ? { htmlBody: htmlBody.trim() } : {}),
    target,
    category,
    ...(isTest ? { isTest: true } : {}),
  });

  const handleSendTestToSelf = async () => {
    if (!user?.uid) {
      toast.error('No current admin user');
      return;
    }
    if (subjectEmpty || bodyEmpty) {
      toast.error('Subject and body are required');
      return;
    }
    setTesting(true);
    try {
      const result = await callSendEmailToUsers(
        buildParams({ userIds: [user.uid] }, true),
      );
      setLastResult(result);
      setHistoryKey((k) => k + 1);
      if (result.queuedCount === 0) {
        // Not a failure — the gate did exactly what it should. Say so plainly,
        // because "sent 0" with no explanation reads as a broken feature.
        toast.error(
          `Nothing queued: your own account has not opted in to ${category} email. Turn it on in the app under Profile → Notification Settings, then retry.`,
          { duration: 8000 },
        );
      } else {
        toast.success('Test queued — check your inbox');
      }
    } catch (err) {
      console.error('Test email failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send test');
    } finally {
      setTesting(false);
    }
  };

  const handleConfirmSend = async () => {
    setSending(true);
    try {
      const result = await callSendEmailToUsers(
        buildParams(resolveTarget(), false),
      );
      setLastResult(result);
      setHistoryKey((k) => k + 1);
      setShowConfirm(false);
      toast.success(
        `Queued ${result.queuedCount} of ${result.resolvedUserCount} resolved`,
      );
    } catch (err) {
      console.error('Email send failed:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Email</h1>
        <p className="text-gray-500 mt-1">
          Compose and send an email to app users. Every message carries the
          postal address and, for these categories, a one-click unsubscribe
          link.
        </p>
      </div>

      <div className="bg-white shadow rounded-lg p-6 space-y-5 max-w-2xl">
        {/* Category — first, because it decides who "all users" even means */}
        <div>
          <label className={labelClass}>Category</label>
          <div className="flex flex-col gap-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="category"
                checked={category === 'marketing'}
                onChange={() => setCategory('marketing')}
                className="mt-1 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">Marketing</span>
                <span className="block text-gray-500">
                  News, offers, anything promotional. Opt-in only — reaches
                  users who explicitly turned it on.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="category"
                checked={category === 'product'}
                onChange={() => setCategory('product')}
                className="mt-1 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">Product</span>
                <span className="block text-gray-500">
                  Service messages about a user's own collection and data.
                  Reaches everyone who has not opted out.
                </span>
              </span>
            </label>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Account and security email (verification codes, closure notices) is
            sent by the flows that produce it and cannot be composed here.
          </p>
        </div>

        {/* Subject */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Subject
            </label>
            <span
              className={`text-xs ${
                subjectOver ? 'text-red-600 font-medium' : 'text-gray-400'
              }`}
            >
              {subject.length}/{SUBJECT_MAX}
            </span>
          </div>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
              subjectOver ? 'border-red-400' : 'border-gray-300'
            }`}
          />
        </div>

        {/* Plain-text body */}
        <div>
          <label className={labelClass}>Body (plain text)</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            placeholder={'Hi,\n\nBlank lines start a new paragraph.'}
            className={inputClass}
          />
          <p className="text-xs text-gray-500 mt-1">
            Always sent as the plain-text part. Without custom HTML below, it
            also becomes the HTML part — blank lines become paragraphs and
            angle brackets are escaped, so prose like "ships in &lt; 5 days" is
            safe.
          </p>
        </div>

        {/* Optional HTML */}
        <div>
          <label className="flex items-center gap-2 cursor-pointer mb-1">
            <input
              type="checkbox"
              checked={useHtml}
              onChange={(e) => setUseHtml(e.target.checked)}
              className="text-brand-600 focus:ring-brand-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Write the HTML part by hand
            </span>
          </label>
          {useHtml && (
            <>
              <textarea
                value={htmlBody}
                onChange={(e) => setHtmlBody(e.target.value)}
                rows={8}
                placeholder="<p>Hi,</p>"
                className={`${inputClass} font-mono text-xs`}
              />
              <p className="text-xs text-gray-500 mt-1">
                Passed through untouched. The footer and unsubscribe link are
                appended by the server either way.
              </p>
            </>
          )}
        </div>

        {/* Recipients */}
        <div>
          <label className={labelClass}>Recipients</label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                checked={targetMode === 'specific'}
                onChange={() => setTargetMode('specific')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">Specific users</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                checked={targetMode === 'inactive'}
                onChange={() => setTargetMode('inactive')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">
                Users inactive for a while
              </span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="target"
                checked={targetMode === 'all'}
                onChange={() => setTargetMode('all')}
                className="text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-gray-700">All consenting users</span>
            </label>
          </div>

          {targetMode === 'specific' && (
            <div className="mt-3">
              <UserMultiSelect
                selected={selectedUsers}
                onChange={setSelectedUsers}
                maxSelection={MAX_SELECTION}
                disabled={sending}
              />
            </div>
          )}

          {targetMode === 'inactive' && (
            <div className="mt-3">
              <InactiveUserPicker onChange={setInactiveUids} />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={sendDisabled || sending}
          >
            Send
          </Button>
          <Button
            variant="secondary"
            onClick={handleSendTestToSelf}
            disabled={subjectEmpty || bodyEmpty || testing}
          >
            {testing ? 'Sending…' : 'Send test to me'}
          </Button>
        </div>
      </div>

      {/* Result */}
      {lastResult && (
        <div className="bg-white shadow rounded-lg p-6 max-w-2xl">
          <h2 className="text-sm font-semibold text-gray-900 mb-3">
            Last send{lastResult.isTest ? ' (test)' : ''}
          </h2>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-gray-500">Queued for delivery</dt>
            <dd className="text-gray-900 font-medium">
              {lastResult.queuedCount}
            </dd>
            <dt className="text-gray-500">Resolved from target</dt>
            <dd className="text-gray-900">{lastResult.resolvedUserCount}</dd>
            <dt className="text-gray-500">Skipped — no consent</dt>
            <dd className="text-gray-900">{lastResult.suppressedCount}</dd>
            <dt className="text-gray-500">Skipped — no email address</dt>
            <dd className="text-gray-900">{lastResult.noEmailCount}</dd>
          </dl>
          {lastResult.suppressedCount > 0 && (
            <p className="text-xs text-gray-500 mt-3">
              Skipped users have not opted in to {lastResult.category} email, or
              their account is closed. That is the consent gate working, not a
              failure.
            </p>
          )}
        </div>
      )}

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">
          Recent sends
        </h2>
        <EmailHistory reloadKey={historyKey} />
      </div>

      <Modal
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Send this email?"
      >
        <div className="space-y-4">
          <dl className="text-sm space-y-1">
            <div className="flex gap-2">
              <dt className="text-gray-500 w-24">Category</dt>
              <dd className="text-gray-900 font-medium capitalize">
                {category}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500 w-24">Recipients</dt>
              <dd className="text-gray-900">{recipientSummary}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-gray-500 w-24">Subject</dt>
              <dd className="text-gray-900">{subject}</dd>
            </div>
          </dl>
          <p className="text-xs text-gray-500">
            Anyone who has not consented to {category} email is dropped by the
            server, so the number actually sent may be lower.
          </p>
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setShowConfirm(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} disabled={sending}>
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
